import React from "react";
import styled, { css } from "styled-components";
import { style } from "../style";

type Props = {
  /**
   * The name attached to the input.
   */
  name?: string;

  /**
   * The id attached to the input.
   */
  id?: string;

  /**
   * The type of input to render.
   */
  type: "number" | "text" | "password" | "email" | "tel" | "url" | "search" | "textarea";

  /**
   * The value of the input.
   */
  value: number | string;

  /**
   * Whether the input will autoComplete (only works on Safari).
   * @default "off"
   */
  autoComplete?: string;

  /**
   * Whether the input should spellcheck.
   * @default false
   */
  spellCheck?: boolean;

  /**
   * Placeholder text for the input.
   * @default ""
   */
  placeholder?: string;

  /**
   * Minimum valid length of the input.
   * @default 0
   */
  minLength?: number;

  /**
   * Maximum valid length of the input.
   * @default Infinity
   */
  maxLength?: number;

  /**
   * A regular expression to validate string input against.
   * @default undefined
   */
  pattern?: string;

  /**
   * The minimum valid value of the input. Number only.
   * @default undefined
   */
  min?: number;

  /**
   * The maximum valid value of the input. Number only.
   * @default undefined
   */
  max?: number;

  /**
   * The step size of the input. Number only.
   * @default undefined
   */
  step?: number;

  /**
   * The number of rows of content to display. TextArea only.
   */
  rows?: number;

  /**
   * Whether the input is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the input accepts multiple, comma-separated values.
   * @default false
   */
  multiple?: boolean;

  /**
   * Whether the input is required.
   * @default false
   */
  required?: boolean;

  /**
   * Whether the input is read-only.
   * @default false
   */
  readOnly?: boolean;

  /**
   * Whether the input uses a monospace font.
   * @default false
   * @private
   */
  mono?: boolean;

  /**
   * An element to display on the leftSide of the input.
   * @default undefined
   */
  leftSide?: React.ReactNode;

  /**
   * An element to display on the rightSide of the input.
   * @default undefined
   */
  rightSide?: React.ReactNode;

  /**
   * The tabIndex for the input.
   * @default 0
   */
  tabIndex?: 0 | -1;

  /**
   * Whether the input should display as invalid.
   * @private
   * @default false
   */
  invalid?: boolean;

  /**
   * Internal use only.
   * @private
   */
  UNSAFE_borderRadiusBottomRight?: "none" | "base";
  /**
   * Internal use only.
   * @private
   */
  UNSAFE_borderRadiusBottomLeft?: "none" | "base";
  /**
   * Internal use only.
   * @private
   */
  UNSAFE_borderRadiusTopRight?: "none" | "base";
  /**
   * Internal use only.
   * @private
   */
  UNSAFE_borderRadiusTopLeft?: "none" | "base";
  /**
   * Internal use only.
   * @private
   */
  UNSAFE_center?: boolean;
  /**
   * Internal use only.
   * @private
   */
  UNSAFE_className?: string;

  /**
   * Internal use only.
   * @private
   */
  "aria-hidden"?: boolean;

  /**
   * Testing utility tag.
   * @default undefined
   */
  "data-testid"?: string;

  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyPress?: React.KeyboardEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onKeyUp?: React.KeyboardEventHandler<HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
  onInvalid?: React.FormEventHandler<HTMLInputElement>;
};

type ContainerProps = {
  $invalid: boolean;
  $disabled: boolean;
  $textArea: boolean;
  $borderRadiusBottomLeft: "none" | "base";
  $borderRadiusBottomRight: "none" | "base";
  $borderRadiusTopLeft: "none" | "base";
  $borderRadiusTopRight: "none" | "base";
};

type InputProps = {
  $monospace: boolean;
  $center: boolean;
  $textArea: boolean;
  $emptyLeftSide: boolean;
  $emptyRightSide: boolean;
};

const Container = styled.div<ContainerProps>`
  all: unset;
  width: 100%;
  padding: 0;
  display: flex;
  position: relative;
  gap: ${style.vars.whitespace.regular};

  box-sizing: border-box;
  border: 1px solid ${style.colors.grey.dark};
  background-color: ${style.colors.white};
  cursor: text;

  &&:focus,
  &&:focus-within {
    outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
    outline-offset: 1px;
    z-index: ${style.vars.zIndex.raised};
  }

  ${(props) => {
    return {
      borderTopLeftRadius: props.$borderRadiusTopLeft === "base" ? style.vars.borderRadius.base : 0,
      borderTopRightRadius: props.$borderRadiusTopRight === "base" ? style.vars.borderRadius.base : 0,
      borderBottomLeftRadius: props.$borderRadiusBottomLeft === "base" ? style.vars.borderRadius.base : 0,
      borderBottomRightRadius: props.$borderRadiusBottomRight === "base" ? style.vars.borderRadius.base : 0,

      height: props.$textArea ? undefined : 48,
    };
  }}

  &:hover,
  &:focus,
  &:focus-within {
    border-color: ${style.colors.blue.dark};
  }

  ${(props) => {
    if (props.$disabled) {
      return css`
        && {
          opacity: 0.3; /* Overwrite existing style */
          color: ${style.colors.grey.primary};
          border-color: ${style.colors.grey.primary};
          background-color: ${style.colors.grey.lightest};
          cursor: not-allowed;
        }
      `;
    }

    if (props.$invalid) {
      return css`
        && {
          border-color: ${style.colors.red.dark};
        }
      `;
    }
  }}
`;

const Input = styled.input<InputProps>`
  box-sizing: border-box;

  flex-grow: 1;

  /* removing global overrides */
  &&&& {
    transition: unset;
    outline: none;
    width: 100%;
    height: 100%;
    display: block;
    border: unset;
    box-shadow: unset;
    line-height: ${style.vars.lineHeight};

    &::placeholder {
      color: ${style.colors.grey.primary};
    }

    /* remove arrows from number inputs */
    &,
    &::-webkit-inner-spin-button {
      appearance: none;
    }

    /* remove resize handle from textarea */
    resize: none;

    ${(props) => {
      return {
        textAlign: props.$center ? "center" : undefined,
        fontFamily: props.$monospace ? style.vars.monoFont : undefined,

        /**
         * Due to the line height of the standard text input, when this is a text area, we need this
         * to have 11px of padding on the top and bottom when there is a single row so that it looks like
         * the text input.
         */
        padding: props.$textArea ? `11px 0` : 0,
        /**
         * We want the input to be the full width of the container if there is no left or right side icons
         * so that the entire area is clickable.
         */
        paddingLeft: props.$emptyLeftSide ? style.vars.whitespace.regular : 0,
        paddingRight: props.$emptyRightSide ? style.vars.whitespace.regular : 0,
      };
    }}
  }
`;

const LeftSide = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding-left: ${(props) => (React.Children.count(props.children) > 0 ? style.vars.whitespace.regular : 0)};
`;

const RightSide = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding-right: ${(props) => (React.Children.count(props.children) > 0 ? style.vars.whitespace.regular : 0)};
`;

export const LooseTextInput = React.forwardRef<HTMLInputElement, Props>((props, forwardedRef) => {
  const {
    type = "text",
    placeholder = "",
    name,
    id,
    value,
    onChange,
    onFocus,
    onBlur,
    onKeyPress,
    onKeyDown,
    onKeyUp,
    onPaste,
    onInvalid,
    min,
    max,
    step,
    pattern,
    minLength,
    maxLength,
    tabIndex,
    rows,
    invalid = false,
    multiple = false,
    required = false,
    disabled = false,
    mono = false,
    spellCheck = false,
    readOnly = false,
    leftSide = null,
    rightSide = null,
    autoComplete = "off",
    UNSAFE_borderRadiusBottomRight = "base",
    UNSAFE_borderRadiusBottomLeft = "base",
    UNSAFE_borderRadiusTopRight = "base",
    UNSAFE_borderRadiusTopLeft = "base",
    UNSAFE_center = false,
    UNSAFE_className,
  } = props;

  const textArea = type === "textarea";

  return (
    <Container
      $disabled={disabled}
      $invalid={invalid}
      $textArea={textArea}
      $borderRadiusBottomLeft={UNSAFE_borderRadiusBottomLeft}
      $borderRadiusTopLeft={UNSAFE_borderRadiusTopLeft}
      $borderRadiusBottomRight={UNSAFE_borderRadiusBottomRight}
      $borderRadiusTopRight={UNSAFE_borderRadiusTopRight}
    >
      {leftSide && <LeftSide>{leftSide}</LeftSide>}
      <Input
        {...(textArea ? { as: "textarea", rows, $textArea: true } : { as: "input", type, $textArea: false })}
        data-testid={props["data-testid"]}
        id={id}
        className={UNSAFE_className}
        tabIndex={tabIndex}
        name={name}
        ref={forwardedRef}
        placeholder={placeholder}
        value={value}
        min={min}
        max={max}
        step={step}
        minLength={minLength}
        maxLength={maxLength}
        pattern={pattern}
        aria-invalid={invalid}
        aria-hidden={props["aria-hidden"]}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyPress={onKeyPress}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onPaste={onPaste}
        onInvalid={onInvalid}
        multiple={multiple}
        disabled={disabled}
        required={required}
        readOnly={readOnly}
        autoComplete={autoComplete}
        spellCheck={spellCheck}
        $center={UNSAFE_center}
        $monospace={mono}
        $emptyLeftSide={!leftSide}
        $emptyRightSide={!rightSide}
      />
      {rightSide && <RightSide>{rightSide}</RightSide>}
    </Container>
  );
});

LooseTextInput.displayName = "LooseTextInput";
